import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useCollection } from '../../hooks/useFirestore'
import { useProject } from '../../context/ProjectContext'
import { Icon } from '../common'
import './EntityLinker.css'

/**
 * Get image URL from various formats (string or { url, publicId } object)
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || ''
}

/**
 * EntityLinker - Multi-select for linking related entities
 * 
 * @param {string} label - Display label
 * @param {string} linkType - The field name (e.g., 'linkedCharacters')
 * @param {string} entityType - Collection name ('characters', 'locations', etc.)
 * @param {array} value - Currently selected entity IDs
 * @param {function} onChange - Change handler
 * @param {string} accent - 'cyan' | 'amber'
 */
export function EntityLinker({
  label,
  linkType,
  entityType,
  value = [],
  onChange,
  accent = 'cyan',
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef(null)
  const inputRef = useRef(null)
  
  // Get project context for scoped queries
  const { projectId } = useProject()

  // Fetch entities from Firestore (project-scoped)
  const { data: entities, loading } = useCollection(projectId, entityType, {
    orderByField: entityType === 'events' || entityType === 'shots' ? 'title' : 'name',
  })

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter entities by search
  const filteredEntities = entities.filter(entity => {
    const name = entity.name || entity.title || ''
    return name.toLowerCase().includes(search.toLowerCase())
  })

  // Get selected entities for display
  const selectedEntities = entities.filter(e => value.includes(e.id))

  // Toggle entity selection
  const handleToggle = (entityId) => {
    if (value.includes(entityId)) {
      onChange(value.filter(id => id !== entityId))
    } else {
      onChange([...value, entityId])
    }
  }

  // Remove selected entity
  const handleRemove = (entityId) => {
    onChange(value.filter(id => id !== entityId))
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  // Type icon names
  const typeIconNames = {
    characters: 'node-diamond',
    locations: 'locations',
    events: 'node-circle',
    shots: 'shots',
  }

  // Animation variants
  const dropdownVariants = {
    hidden: { 
      opacity: 0, 
      y: -10,
      scale: 0.95,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25,
      },
    },
    exit: { 
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  }

  const badgeVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { type: 'spring', stiffness: 400, damping: 25 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      transition: { duration: 0.1 }
    },
  }

  return (
    <div 
      className={`entity-linker entity-linker--${accent}`} 
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <label className="entity-linker__label">
        <span className="entity-linker__icon"><Icon name={typeIconNames[entityType]} size={14} /></span>
        {label}
      </label>

      {/* Selected badges */}
      <div className="entity-linker__selected">
        <AnimatePresence mode="popLayout">
          {selectedEntities.map(entity => (
            <motion.span
              key={entity.id}
              className="entity-linker__badge"
              variants={badgeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              layout
            >
              <span className="entity-linker__badge-name">
                {entity.name || entity.title}
              </span>
              <button
                type="button"
                className="entity-linker__badge-remove"
                onClick={() => handleRemove(entity.id)}
                aria-label={`Remove ${entity.name || entity.title}`}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path 
                    d="M9 3L3 9M3 3L9 9" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </motion.span>
          ))}
        </AnimatePresence>

        {/* Add button / Dropdown trigger */}
        <button
          type="button"
          className="entity-linker__add-btn"
          onClick={() => {
            setIsOpen(!isOpen)
            setTimeout(() => inputRef.current?.focus(), 100)
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path 
              d="M7 1V13M1 7H13" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
            />
          </svg>
          <span>Add</span>
        </button>
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="entity-linker__dropdown"
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Search input */}
            <div className="entity-linker__search">
              <svg 
                className="entity-linker__search-icon" 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none"
              >
                <path 
                  d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M14 14L11.1 11.1" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="entity-linker__search-input"
                placeholder={`Search ${entityType}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Options list */}
            <div className="entity-linker__options">
              {loading ? (
                <div className="entity-linker__loading">Loading...</div>
              ) : filteredEntities.length === 0 ? (
                <div className="entity-linker__empty">
                  {search ? 'No matches found' : `No ${entityType} available`}
                </div>
              ) : (
                filteredEntities.map(entity => {
                  const isSelected = value.includes(entity.id)
                  return (
                    <button
                      key={entity.id}
                      type="button"
                      className={`entity-linker__option ${isSelected ? 'entity-linker__option--selected' : ''}`}
                      onClick={() => handleToggle(entity.id)}
                    >
                      {/* Checkbox indicator */}
                      <span className="entity-linker__checkbox">
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path 
                              d="M2 6L5 9L10 3" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>

                      {/* Entity image */}
                      {getImageUrl(entity.primaryImage) ? (
                        <img 
                          src={getImageUrl(entity.primaryImage)}
                          alt=""
                          className="entity-linker__option-image"
                        />
                      ) : (
                        <span className="entity-linker__option-placeholder">
                          <Icon name={typeIconNames[entityType]} size={16} />
                        </span>
                      )}

                      {/* Entity name */}
                      <span className="entity-linker__option-name">
                        {entity.name || entity.title}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default EntityLinker

